import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { Card, CardContent, Stack } from '@mui/material';

import { QueryRenderer } from '@barboxx/admin-components';
import Api from '@barboxx/api';
import { Business } from '@barboxx/models';
import { BUSINESS_PICKUP_MODE } from '@barboxx/types';

import { BUSINESS_SCHEMA, BusinessForm } from './BusinessForm';
import { LockerTable } from './LockerTable';
import { PlaceInfo } from './PlaceInfo';

// Generated by Copilot

interface Props {
    business: Business.DetailsResponse;
    refetch: () => void;
}

export function BusinessInfoContent({ business, refetch }: Props) {
    const notifications = useNotifications();

    const formContext = useForm<Business.UpdateRequest>({
        resolver: yupResolver(BUSINESS_SCHEMA),
        defaultValues: {
            ...business,
            pickupMode: business.pickupMode ?? BUSINESS_PICKUP_MODE.Nfc,
            orderSalesCutoffBufferMinutes:
                business.orderSalesCutoffBufferMinutes ?? 15,
        },
        context: business.place,
    });

    const mutateBusiness = useMutation({
        mutationKey: ['business', business.id],
        mutationFn: (data: Business.UpdateRequest) =>
            Api.Businesses.updateBusiness(business.id, data),
    });

    const handleFormSave = async (values: Business.UpdateRequest) => {
        try {
            const { data } = await mutateBusiness.mutateAsync(values);
            notifications.show(`Updated business details`, {
                severity: 'success',
            });
            formContext.reset(data);
            return true;
        } catch {
            notifications.show('Error updating business!', {
                severity: 'error',
            });
            return false;
        }
    };

    return (
        <Stack gap={2}>
            <PlaceInfo business={business} />
            <Card>
                <CardContent>
                    <BusinessForm
                        formContext={formContext}
                        onSubmit={handleFormSave}
                        hoursOfOperations={
                            business.place?.hoursOfOperations ?? null
                        }
                    />
                </CardContent>
            </Card>
            <LockerTable lockers={business?.lockers ?? []} refetch={refetch} />
        </Stack>
    );
}

export function BusinessInfo() {
    const { id } = useParams<{ id: string }>();
    const businessId = Number(id);

    const businessQueryResults = useQuery({
        queryKey: ['business', businessId],
        queryFn: () => Api.Businesses.getBusiness(businessId),
    });

    return (
        <QueryRenderer results={businessQueryResults}>
            {data => (
                <BusinessInfoContent
                    business={data}
                    refetch={businessQueryResults.refetch}
                />
            )}
        </QueryRenderer>
    );
}
